import axios, { AxiosResponse } from 'axios';

import { logger } from './logger';

/**
 * OAuth Token Response from backend
 */
export interface OAuthTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scope?: string;
}

/**
 * Cached OAuth token data
 */
interface OAuthTokenData {
  accessToken: string;
  tokenType: string;
  expiresAt: number;
  refreshToken?: string;
  scope?: string;
}

/**
 * Check if we're running during build time (Docker build)
 */
function isBuildTime(): boolean {
  // Explicit flag set during Docker/CI builds
  if (process.env.DOCKER_BUILD === 'true' || process.env.BUILD_TIME === 'true') {
    return true;
  }

  // Next.js build phase
  if (process.env.NEXT_PHASE === 'build' || process.env.NEXT_BUILD === 'true') {
    return true;
  }

  return false;
}

/**
 * OAuthTokenManager handles OAuth 2.0 client credentials flow
 * for the API backend.
 *
 * SECURITY CRITICAL:
 * - Uses OAUTH_CLIENT_ID and OAUTH_CLIENT_SECRET from environment variables (server-side only)
 * - NEVER expose credentials to client
 * - This class should ONLY be instantiated on the server
 * - Manages OAuth tokens with automatic refresh
 */
export class OAuthTokenManager {
  private static instance: OAuthTokenManager;
  private tokenData: OAuthTokenData | null = null;
  /** In-flight token fetch — deduplicates concurrent callers */
  private pendingFetch: Promise<void> | null = null;
  private readonly clientId: string;
  private readonly clientSecret: string;
  private readonly grantType: string;
  private readonly tokenUrl: string;
  private readonly scope: string;
  protected readonly logger = logger.child({ module: 'OAuthTokenManager' });

  private constructor() {
    // SECURITY: Prevent client-side instantiation
    if (typeof window !== 'undefined') {
      throw new Error('OAuthTokenManager cannot be used on client side. This is a server-only module.');
    }

    // Load OAuth configuration from environment variables
    this.clientId = process.env.OAUTH_CLIENT_ID || '';
    this.clientSecret = process.env.OAUTH_CLIENT_SECRET || '';
    this.grantType = process.env.OAUTH_GRANT_TYPE || 'client_credentials';
    this.scope = process.env.OAUTH_SCOPE || 'read write';
    this.tokenUrl = process.env.OAUTH_TOKEN_URL || '';

    // Validate configuration
    if (!this.tokenUrl) {
      this.logger.warn({ msg: 'OAuth token URL not configured. Set OAUTH_TOKEN_URL environment variable.' });
    }
    if (!this.clientId || !this.clientSecret) {
      this.logger.warn({
        msg: 'OAuth credentials not configured. Set OAUTH_CLIENT_ID and OAUTH_CLIENT_SECRET environment variables.',
        hasClientId: !!this.clientId,
        hasClientSecret: !!this.clientSecret,
      });
    }

    this.logger.debug({
      msg: 'OAuthTokenManager initialized',
      tokenUrl: this.tokenUrl,
      grantType: this.grantType,
      scope: this.scope,
    });
  }

  static getInstance(): OAuthTokenManager {
    if (!OAuthTokenManager.instance) {
      OAuthTokenManager.instance = new OAuthTokenManager();
    }
    return OAuthTokenManager.instance;
  }

  /**
   * Get or refresh OAuth access token
   * Uses client credentials grant flow
   */
  async getAccessToken(): Promise<string> {
    // During build time, return a dummy token to prevent API calls
    if (isBuildTime()) {
      logger.debug({ msg: 'Build time detected, returning dummy OAuth token' });
      return 'build-time-oauth-token';
    }

    // Validate configuration
    if (!this.clientId || !this.clientSecret) {
      throw new Error('OAuth credentials not configured');
    }

    // Check if token exists and is not expired (with 5 minute buffer)
    if (this.tokenData && this.tokenData.expiresAt > Date.now() + 5 * 60 * 1000) {
      this.logger.debug({ msg: 'Using cached OAuth token' });
      return this.tokenData.accessToken;
    }

    // Token doesn't exist or is expired — deduplicate concurrent fetches
    if (!this.pendingFetch) {
      this.logger.debug({ msg: 'Fetching new OAuth token' });
      this.pendingFetch = this.fetchTokenWithRetry().finally(() => {
        this.pendingFetch = null;
      });
    } else {
      this.logger.debug({ msg: 'Waiting for in-flight OAuth token fetch' });
    }

    await this.pendingFetch;

    if (!this.tokenData) {
      throw new Error('Failed to fetch OAuth token');
    }

    return this.tokenData.accessToken;
  }

  /**
   * Determine delay (ms) before the next retry.
   * Respects the Retry-After response header when present.
   */
  private retryDelay(attempt: number, retryAfterHeader?: string): number {
    // Retry-After can be a number of seconds or an HTTP date string
    if (retryAfterHeader) {
      const seconds = Number(retryAfterHeader);
      if (!isNaN(seconds)) {
        return seconds * 1000;
      }
      const date = Date.parse(retryAfterHeader);
      if (!isNaN(date)) {
        return Math.max(0, date - Date.now());
      }
    }
    // Exponential backoff: 1000 ms * 2^attempt + jitter (up to 2 s)
    return Math.min(1000 * Math.pow(2, attempt) + Math.random() * 2000, 30_000);
  }

  /**
   * Fetch new OAuth token using client credentials flow with exponential-backoff
   * retries for 429 (rate-limit) and transient 5xx errors.
   */
  private async fetchTokenWithRetry(maxAttempts = 5): Promise<void> {
    const tokenRequestData = new URLSearchParams({
      grant_type: this.grantType,
      client_id: this.clientId,
      client_secret: this.clientSecret,
      scope: this.scope,
    });

    let lastError: any;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        this.logger.debug({
          msg: 'Requesting OAuth token',
          url: this.tokenUrl,
          grant_type: this.grantType,
          attempt: attempt + 1,
        });

        const response: AxiosResponse<OAuthTokenResponse> = await axios.post(this.tokenUrl, tokenRequestData, {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        });

        // Validate response
        if (!response.data || !response.data.access_token) {
          throw new Error('Invalid OAuth token response: missing access_token');
        }

        const { access_token, token_type, expires_in, refresh_token, scope } = response.data;

        const expiresInSeconds = expires_in || 3600;
        const expiresAt = Date.now() + expiresInSeconds * 1000;

        this.tokenData = {
          accessToken: access_token,
          tokenType: token_type || 'Bearer',
          expiresAt,
          refreshToken: refresh_token,
          scope,
        };

        this.logger.debug({
          msg: 'OAuth token fetched successfully',
          tokenType: token_type,
          expiresIn: expiresInSeconds,
          expiresAt: new Date(expiresAt).toISOString(),
          scope,
          attempt: attempt + 1,
        });

        return; // success
      } catch (error: any) {
        lastError = error;
        const status: number | undefined = error.response?.status;
        const isRateLimit = status === 429;
        const isTransient = status !== undefined && status >= 500 && status < 600;
        const isNetworkError = !status; // no response at all

        if ((isRateLimit || isTransient || isNetworkError) && attempt < maxAttempts - 1) {
          const retryAfterHeader: string | undefined = error.response?.headers?.['retry-after'];
          const delay = this.retryDelay(attempt, retryAfterHeader);

          this.logger.warn({
            msg: `OAuth token fetch failed (${status ?? 'network'}), retrying after ${Math.round(delay)}ms`,
            attempt: attempt + 1,
            maxAttempts,
            status,
            retryAfter: retryAfterHeader ?? null,
            delayMs: Math.round(delay),
          });

          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // Non-retriable error or exhausted retries
        this.logger.error({
          msg: 'Failed to fetch OAuth token',
          error: error.message,
          responseData: error.response?.data,
          status,
          attempt: attempt + 1,
        });
        throw new Error(`OAuth authentication failed: ${error.message}`);
      }
    }

    // Should not be reached, but satisfy TypeScript
    throw new Error(`OAuth authentication failed after ${maxAttempts} attempts: ${lastError?.message}`);
  }

  /**
   * Get the full authorization header value
   * Example: "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
   */
  async getAuthorizationHeader(): Promise<string> {
    const token = await this.getAccessToken();
    const tokenType = this.tokenData?.tokenType || 'Bearer';
    return `${tokenType} ${token}`;
  }

  /**
   * Clear cached token (useful for testing or forcing refresh)
   */
  clearToken(): void {
    this.tokenData = null;
    this.logger.debug({ msg: 'OAuth token cache cleared' });
  }

  /**
   * Check if OAuth is properly configured
   */
  isConfigured(): boolean {
    return !!(this.clientId && this.clientSecret && this.tokenUrl);
  }

  /**
   * Assert configuration is valid, throwing if not.
   * Useful for fail-fast startup validation.
   */
  ensureConfigured(): void {
    const missing: string[] = [];
    if (!this.tokenUrl) missing.push('OAUTH_TOKEN_URL');
    if (!this.clientId) missing.push('OAUTH_CLIENT_ID');
    if (!this.clientSecret) missing.push('OAUTH_CLIENT_SECRET');

    if (missing.length) {
      const message = `OAuth configuration missing required env vars: ${missing.join(', ')}`;
      this.logger.error({ msg: message });
      throw new Error(message);
    }
  }
}
